import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Swords, Copy, Send, Check, Share2, Bot } from "lucide-react";
import api, { TIER_META } from "../lib/api";
import { useRoastShare, ShareCardPortal } from "../hooks/useRoastShare";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const TIERS = ["mild", "medium", "savage", "no_mercy"];

export default function BattleArena() {
  const navigate = useNavigate();
  const [battles, setBattles] = useState([]);
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  const [tier, setTier] = useState("savage");
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);
  const { share, busyId, shareRef, shareItem } = useRoastShare();

  const load = async () => { try { setBattles((await api.get("/battles/mine")).data); } catch (e) {} };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!content.trim()) return toast.error("Add your content first");
    setCreating(true);
    try {
      const b = (await api.post("/battles", { content_type: "text", content, tier })).data;
      toast.success("Challenge created! Share the link with your opponent 🔗");
      setOpen(false); setContent(""); load();
      copyInvite(b.invite_token);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const copyInvite = (token) => {
    navigator.clipboard?.writeText(`${window.location.origin}/battle/join/${token}`)?.catch(() => {});
    toast.success("Challenge link copied — send it to your opponent!");
  };

  const consent = async (b) => {
    try { const r = (await api.post(`/battles/${b.id}/consent`)).data; setDetail(r); load(); toast.success(r.published ? "Both agreed — battle is public! 🔥" : "You're in. Waiting on your opponent to agree."); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const declare = async (b) => {
    try { const r = (await api.post(`/battles/${b.id}/declare`)).data; setDetail(r); load(); toast.success("Result declared 🏆"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const battleCard = (b) => ({ kind: "battle", tier: b.tier, challenger_name: b.challenger_name, opponent_name: b.opponent_name,
    challenger_roast: b.challenger_roast, opponent_roast: b.opponent_roast, ai_judge: b.ai_judge_result, winner: b.winner_declared });

  return (
    <div data-testid="battle-arena">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl flex items-center gap-3"><Swords className="text-brand" /> Roast-Off</h1>
          <p className="text-zinc-400 mt-2">Challenge a friend. Both get roasted. The crowd decides — no cash, just glory.</p>
        </div>
        <button data-testid="new-battle-btn" onClick={() => setOpen(true)} className="px-5 py-2.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors">New challenge</button>
      </div>

      {battles.length === 0 ? (
        <div data-testid="battles-empty" className="rounded-xl border border-white/10 bg-zinc-900/50 p-10 text-center text-zinc-400">
          No battles yet. Throw down a challenge 🔥
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {battles.map((b) => {
            const meta = TIER_META[b.tier] || TIER_META.savage;
            return (
              <div key={b.id} data-testid={`battle-card-${b.id}`} className="rounded-xl border border-white/10 bg-zinc-900/60 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold tracking-widest uppercase px-2.5 py-1 rounded-full" style={{ color: meta.color, background: `${meta.color}22` }}>{meta.label}</span>
                  <span className="text-xs text-zinc-500 capitalize">{b.status.replace("_", " ")}</span>
                </div>
                <div className="font-heading font-bold text-lg">{b.challenger_name} <span className="text-brand">vs</span> {b.opponent_name || "…awaiting opponent"}</div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {b.status === "pending_acceptance" && (
                    <button data-testid={`copy-invite-${b.id}`} onClick={() => copyInvite(b.invite_token)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5"><Copy size={14} /> Copy link</button>
                  )}
                  <button data-testid={`view-battle-${b.id}`} onClick={async () => setDetail((await api.get(`/battles/${b.id}`)).data)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5">View</button>
                  {b.status !== "pending_acceptance" && b.published && (
                    <button data-testid={`spectate-${b.id}`} onClick={() => navigate(`/battle/${b.public_token}`)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-brand/30 text-brand text-sm hover:bg-brand/10">Spectator page</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-battle-dialog" className="bg-zinc-950 border-white/10">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">New Roast-Off</DialogTitle>
            <DialogDescription>Free to enter — no payment, no fee. Both sides get roasted independently, then the crowd picks who took it best.</DialogDescription></DialogHeader>
          <textarea data-testid="battle-content" rows={3} maxLength={600} value={content} onChange={(e) => setContent(e.target.value)} placeholder="What should roasters roast about you?"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-sm focus:border-brand focus:outline-none resize-none" />
          <div className="flex gap-2 flex-wrap">
            {TIERS.map((t) => (
              <button key={t} data-testid={`battle-tier-${t}`} onClick={() => setTier(t)} className={`px-3 py-1.5 rounded-full text-sm border ${tier === t ? "border-brand bg-brand/10 text-white" : "border-white/10 text-zinc-400"}`}>{TIER_META[t].label}</button>
            ))}
          </div>
          <button data-testid="create-battle-submit" onClick={create} disabled={creating} className="w-full py-3 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors disabled:opacity-50">
            {creating ? "Creating…" : <>Create free challenge <Send size={15} className="inline ml-1" /></>}
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent data-testid="battle-detail-dialog" className="bg-zinc-950 border-white/10 max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">{detail.challenger_name} vs {detail.opponent_name || "?"}</DialogTitle>
                <DialogDescription className="capitalize">Status: {detail.status.replace("_", " ")}</DialogDescription></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {[["challenger", detail.challenger_roast, detail.challenger_roaster, detail.challenger_selected], ["opponent", detail.opponent_roast, detail.opponent_roaster, detail.opponent_selected]].map(([side, roast, handle, sel]) => (
                  <div key={side} className="rounded-sm border border-white/10 bg-zinc-900/60 p-3">
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">{side === "challenger" ? detail.challenger_name : detail.opponent_name}</div>
                    {roast ? <p className="text-sm text-zinc-100">"{roast}"<span className="block text-xs text-brand mt-1">{handle}</span></p>
                      : <p className="text-sm text-zinc-500">{sel ? "—" : "Waiting for their winning roast…"}</p>}
                  </div>
                ))}
              </div>
              {detail.ai_judge_result && (
                <div data-testid="battle-ai-judge" className="rounded-sm border border-[#a855f7]/30 bg-[#a855f7]/5 p-3">
                  <div className="text-xs font-bold uppercase tracking-widest text-[#a855f7] flex items-center gap-1.5"><Bot size={13} /> AI Judge</div>
                  <p className="text-sm text-zinc-200 mt-1">Verdict: <b className="capitalize">{detail.ai_judge_result.winner}</b> — {detail.ai_judge_result.reason}</p>
                </div>
              )}
              {detail.status === "resolved" && (
                <button data-testid="battle-consent-btn" onClick={() => consent(detail)} className="w-full py-2.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors">
                  {detail.published ? "Published ✓" : "I agree — publish our Battle Card"}
                </button>
              )}
              {detail.published && (
                <div className="flex gap-2">
                  <button data-testid="battle-share-btn" onClick={() => share(battleCard(detail), "b-" + detail.id)} disabled={busyId === "b-" + detail.id}
                    className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full border border-white/15 hover:bg-white/5 disabled:opacity-50"><Share2 size={15} /> Share card</button>
                  <button data-testid="battle-spectate-btn" onClick={() => navigate(`/battle/${detail.public_token}`)} className="flex-1 py-2.5 rounded-full bg-brand text-black font-bold">Open poll</button>
                </div>
              )}
              {detail.published && !detail.winner_declared && (
                <button data-testid="battle-declare-btn" onClick={() => declare(detail)} className="w-full py-2.5 rounded-full border border-brand/40 text-brand font-bold hover:bg-brand/10">Declare the crowd winner</button>
              )}
              {detail.winner_declared && <div className="text-center text-brand font-bold">🏆 Winner: <span className="capitalize">{detail.winner_declared}</span></div>}
            </>
          )}
        </DialogContent>
      </Dialog>
      <ShareCardPortal shareRef={shareRef} item={shareItem} />
    </div>
  );
}
